import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    ViewChild,
} from '@angular/core';

/**
 * A table cell for a set of short values - a microsegment's ports ("22", "443", "8000-8010") or
 * addresses - each shown as its own monospace chip. It fits as many chips as the column width allows
 * on one line, then collapses the rest into a "+N" chip; clicking "+N" opens a popover listing every
 * value. The fit is measured from the real rendered chip widths and recomputed when the column is
 * resized, so a wider column shows more chips and a narrower one shows fewer. Values are shown verbatim.
 *
 * A click on a value chip falls through to the row (the columns open the detail drawer on any cell
 * click); only the "+N" chip stops that and opens the popover instead, so the peek never fights the row.
 *
 * The popover is a plain fixed-position element placed by hand from the "+N" button's screen position,
 * not a CDK overlay: this app hand-rolls its overlay styles for dialogs and does not carry CDK's
 * connected-position stylesheet, so a connected overlay lands in the corner. A transparent backdrop
 * catches an outside click, and a scroll or resize closes it (a fixed popover cannot follow the row).
 */
@Component({
    selector: 'app-chip-list-cell',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        @if (values.length) {
            <!-- Offscreen copy of every chip (plus a sample "+N") used only to measure natural widths. -->
            <div class="clc-measure" #measure aria-hidden="true">
                @for (v of values; track $index) {
                    <span class="clc-chip">{{ v }}</span>
                }
                <span class="clc-chip clc-more" #moreSample>+9</span>
            </div>
            <div class="clc-track" #track [attr.data-category]="category">
                @for (v of visible; track $index) {
                    <span class="clc-chip">{{ v }}</span>
                }
                @if (hiddenCount > 0) {
                    <button
                        type="button"
                        class="clc-chip clc-more"
                        #moreBtn
                        [class.is-open]="isOpen"
                        [attr.aria-expanded]="isOpen"
                        [attr.aria-label]="hiddenCount + ' more ' + (noun || 'values') + ', show all'"
                        (click)="toggle($event)"
                    >
                        +{{ hiddenCount }}
                    </button>
                }
            </div>
            @if (isOpen) {
                <div class="clc-backdrop" (click)="close()"></div>
                <div
                    class="clc-pop"
                    #pop
                    [attr.data-category]="category"
                    [style.top.px]="popY"
                    [style.left.px]="popX"
                    (click)="$event.stopPropagation()"
                >
                    @if (noun) {
                        <div class="clc-pop-title">{{ title }}</div>
                    }
                    <div class="clc-pop-chips">
                        @for (v of values; track $index) {
                            <span class="clc-chip">{{ v }}</span>
                        }
                    </div>
                </div>
            }
        } @else {
            <span class="clc-empty">{{ emptyLabel }}</span>
        }
    `,
    styles: [
        `
            :host {
                display: flex;
                align-items: center;
                align-self: stretch;
                width: 100%;
                max-width: 100%;
                min-height: 100%;
                min-width: 0;
                position: relative;
            }
            /* The measuring row is laid out (so chips have real widths) but never seen or interactive. */
            .clc-measure {
                position: absolute;
                visibility: hidden;
                pointer-events: none;
                white-space: nowrap;
                top: 0;
                left: 0;
                display: inline-flex;
                gap: var(--clc-gap, 0.25rem);
            }
            .clc-track {
                display: inline-flex;
                align-items: center;
                flex-wrap: nowrap;
                gap: var(--clc-gap, 0.25rem);
                width: 100%;
                min-width: 0;
                overflow: hidden;
            }
            /* One accent per cell drives the chip tint and edge, so source / destination read apart. */
            .clc-track,
            .clc-pop {
                --clc-accent: var(--border-color-strong);
            }
            .clc-track[data-category='source'],
            .clc-pop[data-category='source'] {
                --clc-accent: #0273fb;
            }
            .clc-track[data-category='destination'],
            .clc-pop[data-category='destination'] {
                --clc-accent: #0d9488;
            }
            :host-context(.darkmode) .clc-track[data-category='source'],
            :host-context(.darkmode) .clc-pop[data-category='source'] {
                --clc-accent: #3b9bff;
            }
            :host-context(.darkmode) .clc-track[data-category='destination'],
            :host-context(.darkmode) .clc-pop[data-category='destination'] {
                --clc-accent: #2dd4bf;
            }
            .clc-chip {
                display: inline-flex;
                align-items: center;
                flex: 0 0 auto;
                box-sizing: border-box;
                min-height: 1.75rem;
                font-family: var(--font-mono);
                font-size: 0.8125rem;
                line-height: 1;
                white-space: nowrap;
                color: var(--text-primary);
                background: color-mix(in srgb, var(--clc-accent) 9%, transparent);
                border: 0.0625rem solid color-mix(in srgb, var(--clc-accent) 42%, transparent);
                border-radius: var(--radius-nested);
                padding: 0.15625rem 0.625rem;
            }
            :host-context(.darkmode) .clc-chip {
                background: color-mix(in srgb, var(--clc-accent) 16%, transparent);
                border-color: color-mix(in srgb, var(--clc-accent) 52%, transparent);
            }
            /* The "+N" chip is the one interactive element: neutral at rest, filling with the accent on
               hover or while its popover is open so it reads as the control that reveals the rest. */
            .clc-more {
                --clc-accent: var(--border-color-strong);
                cursor: pointer;
                font-family: var(--font-sans);
                font-weight: 600;
                color: var(--text-secondary);
                background: var(--lt-control-bg, var(--background, #fff));
                transition: background 0.12s, color 0.12s, border-color 0.12s;
            }
            .clc-more:hover,
            .clc-more.is-open {
                color: var(--lt-control-bg, #fff);
                background: var(--border-color-strong);
                border-color: var(--border-color-strong);
            }
            .clc-empty {
                font-family: var(--font-mono);
                font-size: 0.8125rem;
                color: var(--text-secondary);
            }
            /* Transparent full-screen catcher so a click anywhere outside the popover closes it. */
            .clc-backdrop {
                position: fixed;
                inset: 0;
                z-index: 1199;
            }
            /* The popover is a small card listing every value, positioned by hand over everything else so a
               narrow or clipped column never cuts it off. */
            .clc-pop {
                position: fixed;
                z-index: 1200;
                max-width: 20rem;
                padding: 0.625rem;
                background: var(--navigation, #fff);
                border: 0.0625rem solid var(--inputBorder, #d7dbe3);
                border-radius: 0.625rem;
                box-shadow: 0 0.5rem 1.5rem rgba(8, 11, 22, 0.18);
            }
            .clc-pop-title {
                margin-bottom: 0.5rem;
                font-family: var(--font-sans);
                font-size: 0.75rem;
                font-weight: 600;
                color: var(--text-secondary);
            }
            .clc-pop-chips {
                display: flex;
                flex-wrap: wrap;
                gap: 0.25rem;
            }
        `,
    ],
})
export class ChipListCellComponent implements AfterViewInit, OnChanges, OnDestroy {
    /** The values to show, each verbatim as its own chip (e.g. ["22", "443", "8000-8010"]). */
    @Input() values: string[] = [];
    /** Accent for the chips, matching the value-chip cell so the columns read as one system. */
    @Input() category: 'source' | 'destination' = 'destination';
    /** Shown when there are no values (e.g. "-"). */
    @Input() emptyLabel = '-';
    /** What the values are (e.g. "ports"), used for the popover title and the "+N" aria label. Omit for
        no popover title. */
    @Input() noun = '';

    visible: string[] = [];
    hiddenCount = 0;
    isOpen = false;
    popX = 0;
    popY = 0;

    @ViewChild('track') private track?: ElementRef<HTMLElement>;
    @ViewChild('measure') private measure?: ElementRef<HTMLElement>;
    @ViewChild('moreSample') private moreSample?: ElementRef<HTMLElement>;
    @ViewChild('moreBtn') private moreBtn?: ElementRef<HTMLElement>;
    @ViewChild('pop') private pop?: ElementRef<HTMLElement>;

    private resizeObserver?: ResizeObserver;
    private valuesKey = '\u0001';
    private pending = false;
    private destroyed = false;
    private readonly dismiss = () => this.close();

    constructor(private readonly cdr: ChangeDetectorRef, private readonly zone: NgZone) {}

    get title(): string {
        return `All ${this.noun}`;
    }

    ngAfterViewInit(): void {
        const trackEl = this.track?.nativeElement;
        if (trackEl) {
            // Observe outside Angular so a resize never drives change detection on its own; the fit pass
            // renders itself synchronously, so a resize can never loop with a tick.
            this.zone.runOutsideAngular(() => {
                this.resizeObserver = new ResizeObserver(() => this.schedule());
                this.resizeObserver.observe(trackEl);
            });
        }
        // Always measure from a microtask, never straight from this hook, so the fit pass runs after the
        // view is settled and its synchronous render is not nested inside Angular's own change detection.
        this.schedule();
    }

    /** Coalesce recompute requests into a single microtask so a burst of change-detection passes or
        resize notifications can never stack up into a tight loop. */
    private schedule(): void {
        if (this.pending) {
            return;
        }
        this.pending = true;
        Promise.resolve().then(() => {
            this.pending = false;
            this.recompute();
        });
    }

    ngOnChanges(): void {
        // The list table rebuilds the inputs object every change-detection pass, and `values` is an array
        // (compared by reference), so this fires constantly even when the ports are unchanged. Compare the
        // contents and do real work only when they actually change.
        const key = (this.values ?? []).join('\u0000');
        if (key === this.valuesKey) {
            return;
        }
        this.valuesKey = key;
        this.close();
        // Show everything until the next measure, then the fit pass trims it.
        this.visible = this.values;
        this.hiddenCount = 0;
        this.schedule();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.resizeObserver?.disconnect();
        this.removeDismissListeners();
    }

    toggle(event: Event): void {
        event.stopPropagation();
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    close(): void {
        if (!this.isOpen) {
            return;
        }
        this.isOpen = false;
        this.removeDismissListeners();
        if (!this.destroyed) {
            this.cdr.detectChanges();
            // Re-fit now that the popover is gone, in case the column changed width while it was open.
            this.schedule();
        }
    }

    /** Show the popover anchored under the "+N" button, flipped or nudged to stay on screen. */
    private open(): void {
        const btn = this.moreBtn?.nativeElement;
        if (!btn) {
            return;
        }
        this.isOpen = true;
        this.popX = 0;
        this.popY = 0;
        // Render it once so we can measure the card, then place it from the button's screen position.
        this.cdr.detectChanges();
        const b = btn.getBoundingClientRect();
        const card = this.pop?.nativeElement.getBoundingClientRect();
        const w = card?.width ?? 0;
        const h = card?.height ?? 0;
        const margin = 8;
        let left = b.left;
        let top = b.bottom + 4;
        if (left + w > window.innerWidth - margin) {
            left = window.innerWidth - margin - w;
        }
        left = Math.max(margin, left);
        if (top + h > window.innerHeight - margin) {
            // Not enough room below: flip above the button.
            top = b.top - 4 - h;
        }
        top = Math.max(margin, top);
        this.popX = left;
        this.popY = top;
        this.cdr.detectChanges();
        // Close on scroll or resize - the popover is fixed and cannot follow the row. Outside clicks are
        // caught by the backdrop. Listen outside Angular; close() renders itself.
        this.zone.runOutsideAngular(() => {
            window.addEventListener('scroll', this.dismiss, true);
            window.addEventListener('resize', this.dismiss, true);
        });
    }

    private removeDismissListeners(): void {
        window.removeEventListener('scroll', this.dismiss, true);
        window.removeEventListener('resize', this.dismiss, true);
    }

    /** Fit as many chips as the column allows, reserving room for the "+N" chip when any are hidden. */
    private recompute(): void {
        if (this.destroyed || this.isOpen) {
            return;
        }
        const track = this.track?.nativeElement;
        const measure = this.measure?.nativeElement;
        if (!track || !measure) {
            return;
        }
        const available = track.clientWidth;
        const chips = Array.from(measure.querySelectorAll<HTMLElement>('.clc-chip:not(.clc-more)'));
        const widths = chips.map((c) => c.getBoundingClientRect().width);
        const total = widths.length;
        if (!available || !total) {
            return;
        }
        const gap = this.gapPx(track);
        const moreWidth = (this.moreSample?.nativeElement.getBoundingClientRect().width ?? 0) + gap;

        const fit = (reserve: number): number => {
            let used = 0;
            let count = 0;
            for (let i = 0; i < total; i++) {
                const next = widths[i] + (count > 0 ? gap : 0);
                if (used + next + reserve <= available) {
                    used += next;
                    count++;
                } else {
                    break;
                }
            }
            return count;
        };

        let count = fit(0);
        if (count < total) {
            // Some are hidden, so re-fit leaving room for the "+N" chip; always keep at least one value.
            count = Math.max(1, fit(moreWidth));
        }

        const nextHidden = total - count;
        const nextVisible = this.values.slice(0, count);
        if (nextHidden !== this.hiddenCount || nextVisible.length !== this.visible.length) {
            this.visible = nextVisible;
            this.hiddenCount = nextHidden;
            // Render now, synchronously. recompute runs from a microtask (often from the out-of-zone resize
            // observer), so marking for check would not get a tick scheduled and the fit would never paint;
            // detectChanges updates just this cell's view and, because the fit only changed the chips inside
            // a fixed-width track, it cannot resize us and loop.
            this.cdr.detectChanges();
        }
    }

    private gapPx(track: HTMLElement): number {
        const raw = getComputedStyle(track).columnGap || getComputedStyle(track).gap;
        const n = parseFloat(raw);
        return Number.isFinite(n) ? n : 4;
    }
}
